/**
 * Sri Lankan NIC utilities
 * Normalize, validate, and extract Date of Birth and Gender from NIC numbers.
 */

export const normalizeSriLankanNic = (value) =>
  (value || '')
    .toString()
    .trim()
    .replace(/[\s-]/g, '')
    .toUpperCase();

export const isValidSriLankanNic = (value) => {
  const nic = normalizeSriLankanNic(value);
  if (!nic) return false;
  return /^\d{12}$/.test(nic) || /^\d{9}[VX]$/.test(nic);
};

/**
 * Old NIC uses a 2-digit birth year. Map to full year using the same rule as
 * common SL implementations: years above the current year's last two digits
 * belong to the previous century (e.g. 96 → 1996 in 2026, 05 → 2005).
 */
export const resolveOldNicFullYear = (twoDigitYear, referenceDate = new Date()) => {
  const yy = Number(twoDigitYear);
  if (!Number.isFinite(yy) || yy < 0 || yy > 99) return null;

  const currentYear = referenceDate.getFullYear();
  const currentTwoDigit = currentYear % 100;
  return yy > currentTwoDigit ? 1900 + yy : 2000 + yy;
};

/**
 * Gregorian leap-year check (used for Sri Lankan NIC day-number encoding).
 */
export const isLeapYear = (year) => {
  const y = Number(year);
  if (!Number.isFinite(y)) return false;
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
};

/**
 * Convert Sri Lankan NIC day-of-year to a real calendar day-of-year.
 *
 * NIC encoding always reserves day 60 for 29 Feb, even in non-leap years.
 * For non-leap years, days after 60 must be shifted back by 1.
 * Day 60 in a non-leap year is not a valid birth day.
 */
export const nicDayOfYearToCalendarDay = (year, nicDayOfYear) => {
  const y = Number(year);
  let day = Number(nicDayOfYear);
  if (!Number.isFinite(y) || !Number.isFinite(day) || day < 1 || day > 366) {
    return null;
  }

  if (!isLeapYear(y)) {
    if (day === 60) return null;
    if (day > 60) day -= 1;
  }

  return day;
};

/**
 * Convert 1-based day-of-year to YYYY-MM-DD (UTC calendar math, no local TZ drift).
 */
export const dayOfYearToIsoDate = (year, dayOfYear) => {
  const y = Number(year);
  const day = Number(dayOfYear);
  if (!Number.isFinite(y) || !Number.isFinite(day) || day < 1 || day > 366) {
    return null;
  }

  const date = new Date(Date.UTC(y, 0, day));
  if (date.getUTCFullYear() !== y) {
    return null;
  }

  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
};

/**
 * Sri Lankan NIC Parser
 * Extracts Date of Birth and Gender from NIC numbers (Old and New formats)
 */
export const parseNIC = (nic, options = {}) => {
  if (!nic) return null;

  const nicStr = normalizeSriLankanNic(nic);
  const referenceDate = options.referenceDate instanceof Date
    ? options.referenceDate
    : new Date();

  let year;
  let days;
  let gender;

  if (nicStr.length === 10 && /^\d{9}[VX]$/.test(nicStr)) {
    // Old NIC: 853421234V
    const twoDigitYear = parseInt(nicStr.substring(0, 2), 10);
    year = resolveOldNicFullYear(twoDigitYear, referenceDate);
    days = parseInt(nicStr.substring(2, 5), 10);
  } else if (nicStr.length === 12 && /^\d{12}$/.test(nicStr)) {
    // New NIC: 199657332264
    year = parseInt(nicStr.substring(0, 4), 10);
    days = parseInt(nicStr.substring(4, 7), 10);
  } else {
    return null;
  }

  if (!Number.isFinite(year) || !Number.isFinite(days)) {
    return null;
  }

  if (days > 500) {
    gender = 'Female';
    days -= 500;
  } else {
    gender = 'Male';
  }

  const calendarDay = nicDayOfYearToCalendarDay(year, days);
  if (calendarDay == null) return null;

  const dob = dayOfYearToIsoDate(year, calendarDay);
  if (!dob) return null;

  return {
    dob,
    gender,
    year
  };
};

/**
 * Derive CRM personal-info fields from a Sri Lankan NIC.
 * Returns null when the NIC cannot be parsed.
 */
export const getPersonalInfoFromNic = (nic, options = {}) => {
  const parsed = parseNIC(nic, options);
  if (!parsed) return null;

  return {
    dateOfBirth: parsed.dob,
    gender: parsed.gender?.toLowerCase() || ''
  };
};

/**
 * Apply NIC-derived dateOfBirth and gender onto a personalInfo object.
 * When overwriteFromNic is true, overwrites existing values when NIC parses successfully.
 * Otherwise only fills missing dateOfBirth / gender.
 */
export const applyNicDerivedPersonalInfo = (personalInfo = {}, { overwriteFromNic = false } = {}) => {
  const nationalId = personalInfo.nationalId;
  if (!isValidSriLankanNic(nationalId)) {
    return personalInfo;
  }

  const derived = getPersonalInfoFromNic(nationalId);
  if (!derived) {
    return personalInfo;
  }

  const normalizedNic = normalizeSriLankanNic(nationalId);
  const next = {
    ...personalInfo,
    nationalId: normalizedNic
  };

  if (overwriteFromNic || !next.dateOfBirth) {
    next.dateOfBirth = derived.dateOfBirth;
  }

  if (overwriteFromNic || !next.gender) {
    next.gender = derived.gender;
  }

  return next;
};
